import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { FieldValues } from 'react-hook-form';
import { IUseAutoSaveOptions, IUseAutoSaveResult, AutoSaveStatus, log } from './utils';

// Configuration constants
const OFFLINE_RETRY_INTERVAL_MS = 5000;
const ONLINE_RETRY_BASE_MS = 2000;

/**
 * A general-purpose auto-save hook with built-in debouncing, retry, and offline handling.
 *
 * @example
 * ```tsx
 * // Basic usage with react-hook-form
 * const { register, watch } = useForm<ProfileFormData>();
 * const { status, lastSavedAt, isOffline } = useAutoSave({
 *   watch,
 *   saveFn: async (values) => api.updateProfile(values),
 * });
 *
 * // Display save status to user
 * return (
 *   <form>
 *     <input {...register('name')} />
 *     {status === 'saving' && <Spinner />}
 *     {status === 'saved' && <CheckIcon />}
 *     {status === 'error' && <ErrorMessage />}
 *     {isOffline && <OfflineWarning />}
 *   </form>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Advanced usage with initial values and custom config
 * const { status } = useAutoSave({
 *   watch,
 *   saveFn: async (values) => api.saveDraft(values),
 *   initialValues: serverData, // Prevent save on mount
 *   debounceMs: 2000,           // Wait 2s after typing stops
 *   maxRetries: 5,              // Retry up to 5 times on failure
 * });
 * ```
 *
 * ## Status Lifecycle
 *
 * ### Normal Flow (Happy Path)
 * ```
 * idle → saving → saved → idle
 * ```
 * 1. Form changes trigger debounced save
 * 2. Save succeeds on first attempt
 * 3. Briefly shows "saved" status before returning to idle
 *
 * ### API Error Flow (While Online)
 * ```
 * idle → saving → retrying → saving → ... → error → idle
 *                     ↑_________|
 *                 (up to maxRetries)
 * ```
 * 1. Form changes trigger save
 * 2. API fails but network is online
 * 3. Retries with exponential backoff (2s, 4s, 6s...)
 * 4. After maxRetries exceeded: transitions to error
 * 5. User editing form resets error → idle
 *
 * ### Offline Flow (No Network)
 * ```
 * idle → saving → retrying → saving → retrying → ...
 *                     ↑_________|
 *                (infinite until online)
 *
 * When back online and successful:
 * retrying → saving → saved → idle
 * ```
 * 1. Form changes trigger save
 * 2. Network unavailable (navigator.onLine = false)
 * 3. Infinite retries every 5 seconds until connection restored
 * 4. Once online and save succeeds: follows normal success path
 */
export function useAutoSave<T extends FieldValues, F extends FieldValues = T>({
  watch,
  saveFn,
  enabled = true,
  debounceMs = 2000,
  maxRetries = 2, // total retries = maxRetries + 1
  initialValues,
}: IUseAutoSaveOptions<T, F>): IUseAutoSaveResult {
  const [status, setStatus] = useState<AutoSaveStatus>(AutoSaveStatus.Idle);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const retryCountRef = useRef(0);
  const lastSavedRef = useRef<Partial<T> | null>(initialValues ?? null);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false); // ensure one request at a time

  /** Monitor offline/online state */
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /** React Query mutation */
  const mutation = useMutation({
    mutationFn: saveFn,
    retry: false, // important to handle offline
    networkMode: 'always', // important to handle offline
    onSuccess: (_data, values) => {
      log.info('Save successful', values);
      isSavingRef.current = false;
      retryCountRef.current = 0;
      setStatus(AutoSaveStatus.Saved);
      setLastSavedAt(new Date());
      setError(null);
      lastSavedRef.current = values;

      // Instantly revert to idle
      setTimeout(() => setStatus(AutoSaveStatus.Idle), 0);
    },
    onError: async (err, values) => {
      log.error('Save failed', err);
      isSavingRef.current = false;
      setError(err);

      // Clear any existing retry timer
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }

      if (isOffline) {
        // Infinite retry while offline
        setStatus(AutoSaveStatus.Retrying);
        log.info(`(offline) Retrying in ${OFFLINE_RETRY_INTERVAL_MS}ms`);
        retryTimerRef.current = setTimeout(() => triggerSave(values), OFFLINE_RETRY_INTERVAL_MS);
        return;
      }

      if (retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        const delay = ONLINE_RETRY_BASE_MS * retryCountRef.current;
        setStatus(AutoSaveStatus.Retrying);
        log.info(`Retry attempt ${retryCountRef.current}/${maxRetries} in ${delay}ms`);
        // Exponential backoff
        retryTimerRef.current = setTimeout(() => triggerSave(values), delay);
      } else {
        setStatus(AutoSaveStatus.Error);
        log.error(`Max retries (${maxRetries}) reached`);
        retryCountRef.current = 0;
      }
    },
  });

  /** Safe trigger for save (ensures one at a time) */
  const triggerSave = useCallback(
    (values: T) => {
      if (isSavingRef.current || !enabled) return;
      if (lastSavedRef.current && isEqual(values, lastSavedRef.current)) return;

      // Cancel any pending retry timeout (prevents stale data race condition)
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      isSavingRef.current = true;
      setStatus(AutoSaveStatus.Saving);
      log.info('Initiating save...');
      mutation.mutate(values);
    },
    [enabled, mutation],
  );

  /** Debounced handler to prevent spam requests */
  const debouncedSave = useCallback(
    (values: T) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        triggerSave(values);
      }, debounceMs);
    },
    [triggerSave, debounceMs],
  );

  /** Watch form changes */
  useEffect(() => {
    if (!enabled) return;
    const subscription = watch((values) => {
      // Reset if user edits after an error
      if (status === AutoSaveStatus.Error) {
        setStatus(AutoSaveStatus.Idle);
        retryCountRef.current = 0;
      }

      if (status !== AutoSaveStatus.Saving && status !== AutoSaveStatus.Retrying) {
        setStatus(AutoSaveStatus.Idle);
      }

      debouncedSave(values as T);
    });
    return () => subscription.unsubscribe();
  }, [watch, enabled, status, debouncedSave]);

  /** Cleanup timers on unmount */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  return {
    status,
    lastSavedAt,
    error,
    isOffline,
  };
}
